import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, Upload, Users, Activity, AlertTriangle, Wrench,
  Plus, WifiOff, XCircle, CalendarX, Timer, Zap, Heart, Send, Settings
} from "lucide-react";
import { useSubscribers } from "@/context/SubscriberContext";
import { useTickets } from "@/context/TicketContext";
import { formatDateTime } from "@/utils/formatDateTime";
import { StatCard } from "@/components/StatCard";
import { FilterCard } from "@/components/FilterCard";
import { SubscriberRow } from "@/components/SubscriberRow";

const Dashboard = () => {
  const [currentTime, setCurrentTime] = useState(formatDateTime());
  const [sortBy, setSortBy] = useState('issues');
  const [activeFilter, setActiveFilter] = useState(null);
  const navigate = useNavigate();
  const { subscribers } = useSubscribers();
  const { tickets, getDispatchedTickets } = useTickets();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(formatDateTime());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculate stats
  const totalSubscribers = subscribers.length;
  const subscribersWithIssues = subscribers.filter(s => s.issues > 0).length;
  const totalAeds = subscribers.reduce((sum, s) => sum + s.totalAeds, 0);

  // Filter subscribers based on active filter
  const filteredSubscribers = activeFilter
    ? subscribers.filter(s => 
        s.issueTypes.some(issue => issue.type === activeFilter)
      )
    : subscribers;

  // Handle filter click
  const handleFilterClick = (filterType) => {
    setActiveFilter(activeFilter === filterType ? null : filterType);
  };

  const handleDispatchedClick = () => {
    navigate('/dispatched');
  };

  const handleActiveTicketsClick = () => {
    navigate('/active-tickets');
  };

  return (
    <div className="app-container" data-testid="notification-console">
      {/* Header */}
      <header className="header" data-testid="header">
        <div className="header-left">
          <button className="back-button" data-testid="back-button" onClick={() => window.location.href = 'https://cardiac-solutions.ai/hub'}>
            <ArrowLeft size={14} />
            BACK TO HUB
          </button>
          <div className="logo-section">
            <Heart className="logo-icon" fill="#ff4757" />
            <div>
              <div className="logo-text">
                <span className="rescue">RESCUE</span>
                <span className="aid">AID</span>
              </div>
              <div className="logo-subtitle">Service Console V1.0</div>
            </div>
          </div>
        </div>
        <div className="header-right">
          <div className="system-status" data-testid="system-status">
            <span className="status-dot"></span>
            <span>SYSTEM ONLINE</span>
          </div>
          <div className="datetime" data-testid="datetime">
            <Zap size={14} className="bolt-icon" />
            <span>{currentTime}</span>
          </div>
          <div className="settings-version-wrapper">
            <button className="settings-btn" onClick={() => navigate('/settings')} data-testid="settings-btn">
              <Settings size={18} />
            </button>
            <span className="version-label" data-testid="version-label">v26031401</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* Title Section */}
        <h1 className="console-title" data-testid="console-title">
          <Zap size={28} className="bolt" />
          Service Console
        </h1>
        <p className="console-description">
          AED Service Management
        </p>
        <p className="data-timestamp">
          Data from last download: <span>Mar 14, 2026, 5:09:35 AM</span>
        </p>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="action-btn" data-testid="refresh-btn">
            <RefreshCw size={14} className="icon" />
            REFRESH
          </button>
          <button className="action-btn" data-testid="upload-btn">
            <Upload size={14} className="icon" />
          </button>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid" data-testid="stats-grid">
          <StatCard 
            value={totalSubscribers}
            label="SUBSCRIBERS WITH SERVICE ISSUES"
            sublabel={`${subscribersWithIssues} with issues`}
            colorClass="cyan"
            icon={Users}
          />
          <StatCard 
            value={totalAeds}
            label="TOTAL AEDS THAT NEED SERVICE"
            sublabel=""
            colorClass="red"
            icon={Activity}
          />
          <StatCard 
            value={subscribers.reduce((sum, s) => sum + s.needAttention, 0) - tickets.length}
            label="NEED ATTENTION"
            sublabel="Require attention"
            colorClass="red"
            icon={AlertTriangle}
          />
          <StatCard 
            value={tickets.length}
            label="ACTIVE TICKETS"
            sublabel="All active tickets"
            colorClass="yellow"
            icon={Wrench}
            onClick={handleActiveTicketsClick}
          />
          <StatCard 
            value={getDispatchedTickets().length}
            label="DISPATCHED"
            sublabel="Active dispatched tickets"
            colorClass="purple"
            icon={Send}
            onClick={handleDispatchedClick}
          />
          <StatCard 
            value={6}
            label="NEW ISSUES"
            sublabel="Added from today's download"
            colorClass="orange"
            icon={Plus}
          />
        </div>

        {/* Secondary Stats Row - Filter Cards */}
        <div className="stats-grid filter-grid" data-testid="stats-row-secondary">
          <FilterCard 
            value={89}
            label="LOST CONTACT"
            colorClass="cyan"
            icon={WifiOff}
            filterType="lost-contact"
            isActive={activeFilter === 'lost-contact'}
            onClick={() => handleFilterClick('lost-contact')}
          />
          <FilterCard 
            value={28}
            label="NOT READY"
            colorClass="yellow"
            icon={XCircle}
            filterType="not-ready"
            isActive={activeFilter === 'not-ready'}
            onClick={() => handleFilterClick('not-ready')}
          />
          <FilterCard 
            value={45}
            label="EXPIRED B/P"
            colorClass="red"
            icon={CalendarX}
            filterType="expired"
            isActive={activeFilter === 'expired'}
            onClick={() => handleFilterClick('expired')}
          />
          <FilterCard 
            value={23}
            label="EXPIRING B/P"
            colorClass="orange"
            icon={Timer}
            filterType="expiring"
            isActive={activeFilter === 'expiring'}
            onClick={() => handleFilterClick('expiring')}
          />
        </div>

        {/* Active Filter Indicator */}
        {activeFilter && (
          <div className="active-filter-banner" data-testid="active-filter-banner">
            <span>Filtering by: <strong>{activeFilter.replace('-', ' ').toUpperCase()}</strong></span>
            <button onClick={() => setActiveFilter(null)} className="clear-filter-btn">
              Clear Filter
            </button>
          </div>
        )}

        {/* Subscriber Overview */}
        <section className="subscriber-section" data-testid="subscriber-section">
          <div className="subscriber-header">
            <div className="subscriber-title">
              <Zap size={18} className="bolt" />
              <div>
                <h2>SERVICE OVERVIEW</h2>
                <p className="subscriber-count">{totalSubscribers} SUBSCRIBERS MONITORED</p>
              </div>
            </div>
          </div>

          <table className="subscriber-table" data-testid="subscriber-table">
            <thead>
              <tr>
                <th>SUBSCRIBER</th>
                <th>TOTAL AEDS</th>
                <th>ISSUES</th>
                <th>ISSUE TYPES</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscribers
                .sort((a, b) => sortBy === 'issues' ? b.issues - a.issues : a.name.localeCompare(b.name))
                .map(subscriber => (
                  <SubscriberRow key={subscriber.id} subscriber={subscriber} />
                ))}
            </tbody>
          </table>
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
